import {
  IonBadge,
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonCol,
  IonIcon,
  IonLabel,
  IonRouterLink,
  IonRow,
  IonToolbar
} from "@ionic/react";
import {
  addOutline,
  chatbubbles,
  chatbubblesOutline,
  pause,
  play,
  starOutline,
  starSharp
} from "ionicons/icons";
import React, { useEffect, useLayoutEffect, useState } from "react";

import { useComments } from "../data/store/useComments";
import { useFavorites } from "../data/store/useFavorites";
import { usePlayer } from "../data/store/usePlayer";
import { usePlaylist } from "../data/store/usePlaylist";
import { useProfile } from "../data/store/useProfile";
import { useSongs } from "../data/store/useSongs";

import { Song } from "../models/Song";
import MediaService from "../services/MediaService";
import ArtistButton from "./ArtistButton";
import CommentWidget from "./CommentWidget";

interface OwnProps {
  id: string;
}

interface SongItemProps extends OwnProps { }

const CompactSongItem: React.FC<SongItemProps> = ({ id }) => {
  const { isFavorite, addFavorite, removeFavorite } = useFavorites();
  const { authenticated, ownerId } = useProfile();
  const { isPaused } = usePlayer();
  const { fetch, shouldFetch, empty } = useSongs();
  const { setNowPlaying, nowPlayingId, queueSong, songs } = usePlaylist();
  const { retrieve: retrieveComments, observe } = useComments();
  const song = useSongs((x) => x.retrieve(id));
  const commentThread = retrieveComments(id);
  const commentCount = commentThread?.comments?.length || 0;
  const [showComments, setShowComments] = useState(false);
  // const { width } = useWindowSize();

  const isNowPlaying = id === nowPlayingId ? "now-playing" : "";
  const isInPlaylist = song && songs.includes(id);
  const songHeaderClass = "compact song-item-header " + isNowPlaying;
  const isShouldFetch = shouldFetch(id);

  const addSongToPlaylist = () => {
    queueSong(id);
  };

  const pauseNow = () => {
    MediaService.please().pause();
  };

  const playSongNow = () => {
    if (!isInPlaylist) {
      queueSong(id);
    }
    if (song) {
      MediaService.please()
        .setSong(song)
        .then(async () => {
          await MediaService.please().play();
          setNowPlaying(id);
        });
    }
  };

  const renderSongHeader = (song: Song) => {
    return (
      <IonCol style={{ minWidth: 0, overflowX: 'hidden', overflowY: 'visible' }}>
        <IonRouterLink
          routerLink={"/song/" + song.id}
          className="compact-song-title-link"
          title={song.title}
        >
          <span className="compact-song-title-text" aria-label={song.title}>
            {song.title}
          </span>
        </IonRouterLink>

        <div className="compact-alias-row">
          {song.authors &&
            song.authors.map((authorId, index) => (
              <ArtistButton
                key={index}
                artist={song.artists[authorId]}
                variant="compact-alias"
              />
            ))}
        </div>
      </IonCol>
    );
  };

  useLayoutEffect(() => {
    if (!song && isShouldFetch) {
      fetch(id);
    } else if (!song && empty.includes(id)) {
      removeFavorite(ownerId, id);
    }
  }, [empty, fetch, id, isShouldFetch, ownerId, removeFavorite, song]);

  useEffect(() => {
    observe(id);
  }, [id, observe]);

  if (!song) return <></>;

  if (song.privacy === "member" && !authenticated) {
    return (
      <IonCard className="song-item">
        <IonToolbar className={songHeaderClass} />
        <IonCardContent className="song-item-content">
          The author of this song has marked this as visible to members only.
        </IonCardContent>
        <IonButton fill="outline" routerLink="login">
          login
        </IonButton>
      </IonCard>
    );
  }

  return (
    <>
      <div className="compact-song-bubble">
      <IonToolbar className={songHeaderClass}>
        <IonButtons slot="start">
          {isNowPlaying ? (
            isPaused ? (
              <IonButton size="large" onClick={playSongNow} aria-label="Play">
                <IonIcon
                  className="--ion-padding"
                  size="large"
                  color="dark"
                  icon={play}
                  aria-hidden="true"
                />
              </IonButton>
            ) : (
              <IonButton size="large" onClick={pauseNow} aria-label="Pause">
                <IonIcon
                  className="--ion-padding"
                  size="large"
                  color="dark"
                  icon={pause}
                  aria-hidden="true"
                />
              </IonButton>
            )
          ) : (
            <IonButton size="large" onClick={playSongNow} aria-label="Play">
              <IonIcon
                className="--ion-padding"
                size="large"
                color="dark"
                icon={play}
                aria-hidden="true"
              />
            </IonButton>
          )}
        </IonButtons>
        <IonButtons style={{ flex: 1, minWidth: 0, overflowX: 'hidden', overflowY: 'visible' }}>{renderSongHeader(song)}</IonButtons>

        <IonButtons slot="end">
          {!isInPlaylist && (
            <IonButton size="large" onClick={() => addSongToPlaylist()} aria-label="Add to playlist">
              <IonIcon
                className="--ion-padding"
                size="large"
                color={"dark"}
                icon={addOutline}
                aria-hidden="true"
              />
            </IonButton>
          )}

          {ownerId && song.id && (
            <IonButton
              color="dark"
              aria-label={isFavorite(ownerId, song.id) ? "Remove from favorites" : "Add to favorites"}
              onClick={() => {
                !isFavorite(ownerId!, song.id)
                  ? addFavorite(ownerId!, song.id)
                  : removeFavorite(ownerId!, song.id);
              }}
            >
              <IonIcon
                slot="start"
                color={isFavorite(ownerId, song.id) ? "warning" : undefined}
                icon={!isFavorite(ownerId, song.id) ? starOutline : starSharp}
                aria-hidden="true"
              />
            </IonButton>
          )}

          <IonButton onClick={() => setShowComments(!showComments)} color="dark" aria-label="Toggle comments">
            <IonIcon
              icon={commentCount > 0 ? chatbubbles : chatbubblesOutline}
              color={commentCount > 0 ? "primary" : undefined}
              aria-hidden="true"
            />
          </IonButton>
        </IonButtons>
      </IonToolbar>

      {showComments && <CommentWidget id={id} />}
      </div>
    </>
  );
};

export default CompactSongItem;
